import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "@fluentui/react-components";
import { useMe } from "./api/hooks";
import { ensureTeamsInit } from "./auth/teams";
import { AppShell } from "./components/AppShell";
import { MyTicketsPage } from "./pages/EndUser/MyTicketsPage";
import { NewTicketPage } from "./pages/EndUser/NewTicketPage";
import { TicketDetailPage } from "./pages/EndUser/TicketDetailPage";
import { AgentDashboardPage } from "./pages/Agent/AgentDashboardPage";
import { AgentQueuePage } from "./pages/Agent/AgentQueuePage";
import { AdminDashboardPage } from "./pages/Admin/AdminDashboardPage";
import { AdminUsersPage } from "./pages/Admin/AdminUsersPage";
import { AdminCategoriesPage } from "./pages/Admin/AdminCategoriesPage";
import { AdminQueuesPage } from "./pages/Admin/AdminQueuesPage";
import { AdminSlasPage } from "./pages/Admin/AdminSlasPage";
import { AdminReportsPage } from "./pages/Admin/AdminReportsPage";
import { AdminTicketsPage } from "./pages/Admin/AdminTicketsPage";
import { SearchPage } from "./pages/SearchPage";

export default function App() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { ensureTeamsInit(); }, []);

  // Handle deep-link sub-entity ids like "ticket-<id>"
  useEffect(() => {
    if (location.hash.startsWith("#/ticket-")) {
      const id = location.hash.replace("#/ticket-", "");
      navigate(`/tickets/${id}`, { replace: true });
    }
  }, [location, navigate]);

  if (me.isLoading) return <div style={{ padding: 24 }}><Spinner label="Signing in..." /></div>;
  if (me.isError) {
    return (
      <div style={{ padding: 24 }}>
        <h3>Sign-in failed</h3>
        <p>
          Could not authenticate against the API. Confirm the Teams app is sideloaded with the
          correct app registration and that the API is reachable at <code>{import.meta.env.VITE_API_BASE_URL}</code>.
        </p>
      </div>
    );
  }

  const role = me.data!.user.role;

  return (
    <AppShell role={role} displayName={me.data!.user.displayName}>
      <Routes>
        <Route path="/" element={<Navigate to="/my-tickets" replace />} />
        <Route path="/my-tickets" element={<MyTicketsPage />} />
        <Route path="/new" element={<NewTicketPage />} />
        <Route path="/tickets/:id" element={<TicketDetailPage />} />
        <Route path="/search" element={<SearchPage />} />

        {(role === "Agent" || role === "Admin") && (
          <>
            <Route path="/agent" element={<AgentDashboardPage />} />
            <Route path="/agent/queue" element={<AgentQueuePage />} />
          </>
        )}

        {role === "Admin" && (
          <>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/tickets" element={<AdminTicketsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/categories" element={<AdminCategoriesPage />} />
            <Route path="/admin/queues" element={<AdminQueuesPage />} />
            <Route path="/admin/slas" element={<AdminSlasPage />} />
            <Route path="/admin/reports" element={<AdminReportsPage />} />
          </>
        )}

        <Route path="*" element={<Navigate to="/my-tickets" replace />} />
      </Routes>
    </AppShell>
  );
}
